import React from 'react';
import { 
  Box, 
  Container, 
  Grid, 
  Typography, 
  List, 
  ListItem, 
  ListItemIcon, 
  ListItemText, 
  Paper 
} from '@mui/material';
import { 
  CheckCircleOutline, 
  TouchApp, 
  Monitor, 
  Fastfood, 
  PointOfSale, 
  Payment 
} from '@mui/icons-material';
import PublicLayout from "../../layouts/PublicLayout";

export default function QuickService() {
  return (
    <PublicLayout>
      <Box sx={{ bgcolor: '#fff', py: { xs: 8, md: 10 } }}>
        <Container maxWidth="lg">
          <Grid container spacing={8} alignItems="flex-start">
            
            {/* LEFT CONTENT */}
            <Grid item xs={12} md={7}>
              <Typography variant="overline" sx={{ color: '#f6521a', fontWeight: 800, letterSpacing: 1.5 }}>
                TPOS FOR QUICK SERVICE RESTAURANTS
              </Typography>
              
              <Typography variant="h2" sx={{ fontWeight: 600, mt: 1, mb: 2, fontSize: { xs: '1.5rem', md: '2.5rem' }, lineHeight: 1.1 }}>
                Accelerate Your Counter Service with <span style={{ color: "#f6521a" }}>Lightning-Fast Tech.</span>
              </Typography>

              <Typography variant="h6" sx={{ color: 'text.secondary', fontWeight: 500, mb: 4, lineHeight: 1.5 }}>
                Slash queue times, automate kitchen workflows, and maximize peak-hour throughput with our high-speed POS architecture.
              </Typography>

              <Typography variant="body1" sx={{ color: 'text.primary', mb: 5, fontSize: '1.1rem', lineHeight: 1.8 }}>
                In a Quick Service Restaurant (QSR), every second shaved off an order is money earned. High foot traffic requires a system that won't lag, crash, or confuse your cashiers. 
                <strong> TPOS is built explicitly for the rush.</strong> With an ultra-responsive billing interface, dynamic combo logic, customer-facing displays, and integrated token systems, we ensure the hand-off from the cashier to the kitchen to the customer is instantaneous.
              </Typography>

              <Typography variant="h5" sx={{ fontWeight: 700, mb: 3 }}>
                Specialized TPOS Features:
              </Typography>

              <Grid container spacing={3}>
                {[
                  {
                    title: "High-Speed Checkout Interface",
                    desc: "Designed for minimum clicks. A highly optimized, customizable grid layout allows cashiers to punch a complete meal in under 3 taps.",
                    icon: <TouchApp sx={{ color: '#f6521a' }} />
                  },
                  { 
                    title: "Token & Queue Management", 
                    desc: "Automatically print a token number on the receipt and cast 'Preparing' and 'Ready' statuses to a smart TV display.",
                    icon: <Monitor sx={{ color: '#f6521a' }} />
                  },
                  { 
                    title: "Smart Combo & Up-Sell Engine", 
                    desc: "TPOS automatically prompts cashiers to upsell fries and a drink when a standalone burger is selected.",
                    icon: <Fastfood sx={{ color: '#f6521a' }} />
                  },
                  { 
                    title: "Self-Ordering Kiosk Integration", 
                    desc: "Divert line traffic by allowing customers to place and pay for their own orders on touch-screen kiosks powered by TPOS logic.",
                    icon: <PointOfSale sx={{ color: '#f6521a' }} />
                  },
                  { 
                    title: "Integrated Payment Gateways", 
                    desc: "Push the exact bill amount directly to the UPI QR screen or card swipe machine, eliminating manual entry errors.",
                    icon: <Payment sx={{ color: '#f6521a' }} />
                  }
                ].map((item, index) => (
                  <Grid item xs={12} sm={6} key={index}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
                      <Box sx={{ mt: 0.5 }}>{item.icon}</Box>
                      <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{item.title}</Typography>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>{item.desc}</Typography>
                      </Box>
                    </Box>
                  </Grid>
                ))}
              </Grid>

            </Grid>

            {/* RIGHT IMAGE */}
            <Grid item xs={12} md={5} sx={{ position: 'sticky', top: 100 }}>
              <Paper 
                elevation={0} 
                sx={{ 
                  borderRadius: 4, 
                  overflow: 'hidden', 
                  background: 'linear-gradient(145deg, #ffffff 0%, #f9f9f9 100%)',
                  p: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%'
                }}
              >
                <Box
                  sx={{
                    width: '100%',
                    height: { xs: 300, md: 550 },
                    borderRadius: 3,
                    overflow: 'hidden',
                    display: 'flex',
                    alignItems: 'right',
                    float:'right'                    
                  }}
                >
                  <img 
                    src="/img/home-banner.jpg" 
                    alt="QSR POS Management" 
                    style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                  />
                </Box>
                
                <Box sx={{ p: 3, width: '100%' }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>Business Impact</Typography>
                  <List dense>
                    <ListItem disableGutters>
                      <ListItemIcon sx={{ minWidth: 35 }}><CheckCircleOutline sx={{ color: '#00ab55', fontSize: 20 }} /></ListItemIcon>
                      <ListItemText primary="Process up to 3x more orders during peak hours compared to legacy systems." />
                    </ListItem>
                    <ListItem disableGutters>
                      <ListItemIcon sx={{ minWidth: 35 }}><CheckCircleOutline sx={{ color: '#00ab55', fontSize: 20 }} /></ListItemIcon>
                      <ListItemText primary="Boost average ticket size by 15% with automated up-sell prompts and combo deals." />
                    </ListItem>
                  </List>
                </Box>
              </Paper>
            </Grid>

          </Grid>
        </Container>
      </Box>
    </PublicLayout>
  );
}